import React, { useState, useEffect, useRef } from 'react';
import { RetellWebClient } from 'retell-client-js-sdk';
import { API_BASE_URL } from '../config';

const WebCallModal = ({ isOpen, onClose, agent, userEmail }) => {
  const [callStatus, setCallStatus] = useState('');
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');
  const retellClient = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Initialize Retell client
      retellClient.current = new RetellWebClient();
      
      // Set up event listeners
      setupEventListeners();
      
      // Reset state
      setCallStatus('');
      setIsCallActive(false);
      setIsConnecting(false);
      setError('');
    }

    return () => {
      // Cleanup: ensure call is stopped when component unmounts or modal closes
      if (retellClient.current) {
        try {
          retellClient.current.stopCall();
          console.log('Call cleaned up on component unmount');
        } catch (error) {
          console.error('Error during cleanup:', error);
        }
      }
    };
  }, [isOpen]);

  const setupEventListeners = () => {
    const client = retellClient.current;
    
    client.on("call_started", () => {
      console.log("Call started");
      setCallStatus("Call connected! You can now speak.");
      setIsCallActive(true);
      setIsConnecting(false);
    });

    client.on("call_ended", () => {
      console.log("Call ended");
      setCallStatus("Call ended.");
      setIsCallActive(false);
      setIsConnecting(false);
    });

    client.on("agent_start_talking", () => {
      setCallStatus("Agent is speaking...");
    });

    client.on("agent_stop_talking", () => {
      setCallStatus("Listening... You can speak now.");
    });

    client.on("error", (error) => {
      console.error("Call error:", error);
      setError(`Call error: ${error.message || error}`);
      setIsCallActive(false);
      setIsConnecting(false);
    });

    client.on("update", (update) => {
      // Handle real-time updates (transcript, etc.)
      console.log("Call update:", update);
    });
  };

  const startCall = async () => {
    if (!agent || !userEmail) {
      setError('Missing agent or user information');
      return;
    }

    setIsConnecting(true);
    setError('');
    setCallStatus('Creating call...');

    try {
      // Get access token from backend
      const response = await fetch(`${API_BASE_URL}/api/web-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: agent.agent_id,
          user_email: userEmail,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create web call');
      }

      const webCallData = await response.json();
      setCallStatus('Connecting...');

      // Start the call using Retell Web SDK
      await retellClient.current.startCall({
        accessToken: webCallData.access_token,
        sampleRate: 24000,
        emitRawAudioSamples: false,
      });

    } catch (error) {
      console.error('Error starting call:', error);
      setError(error.message);
      setIsConnecting(false);
    }
  };

  const endCall = () => {
    if (retellClient.current) {
      try {
        retellClient.current.stopCall();
        console.log('Call terminated successfully');
      } catch (error) {
        console.error('Error stopping call:', error);
      }
    }
    setCallStatus('Call ended.');
    setIsCallActive(false);
    setIsConnecting(false);
  };

  const handleClose = () => {
    if (isCallActive || isConnecting) {
      if (window.confirm('Call is in progress. Are you sure you want to end it?')) {
        endCall();
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay" 
      onClick={handleClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '32px',
          minWidth: '400px',
          maxWidth: '500px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          marginBottom: '24px',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '16px'
        }}>
          <h2 style={{ 
            margin: 0, 
            color: '#111827', 
            fontSize: '1.5rem', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <i className="fas fa-phone" style={{ color: '#3b82f6' }}></i>
            Web Call with {agent?.agent_name || 'Agent'}
          </h2>
          <button 
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Agent Info */}
        <div style={{ 
          background: '#f8fafc', 
          padding: '16px', 
          borderRadius: '8px', 
          marginBottom: '24px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '8px' }}>
            <strong>Agent:</strong> {agent?.agent_name}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '4px' }}>
            <strong>Language:</strong> {agent?.language || 'en-US'}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
            <strong>Voice:</strong> {agent?.voice_id || 'Default'}
          </div>
        </div>

        {/* Status */}
        {callStatus && (
          <div style={{ 
            background: isCallActive ? '#ecfdf5' : '#f0f9ff', 
            border: `1px solid ${isCallActive ? '#a7f3d0' : '#bae6fd'}`,
            borderRadius: '8px', 
            padding: '12px', 
            marginBottom: '20px' 
          }}>
            <div style={{ 
              color: isCallActive ? '#065f46' : '#0c4a6e', 
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className={`fas ${isCallActive ? 'fa-microphone' : 'fa-info-circle'}`}></i>
              {callStatus}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ 
            background: '#fef2f2', 
            border: '1px solid #fecaca',
            borderRadius: '8px', 
            padding: '12px', 
            marginBottom: '20px' 
          }}>
            <div style={{ 
              color: '#dc2626', 
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          </div>
        )}

        {/* Controls */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          {!isCallActive ? (
            <button 
              onClick={startCall}
              disabled={isConnecting}
              style={{
                background: isConnecting ? '#9ca3af' : '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: isConnecting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: '140px',
                justifyContent: 'center',
              }}
            >
              <i className={`fas ${isConnecting ? 'fa-spinner fa-spin' : 'fa-phone'}`}></i>
              {isConnecting ? 'Connecting...' : 'Start Call'}
            </button>
          ) : (
            <button 
              onClick={endCall}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: '140px',
                justifyContent: 'center',
              }}
            >
              <i className="fas fa-phone-slash"></i>
              End Call
            </button>
          )}
        </div>

        {/* Instructions */}
        <div style={{ 
          marginTop: '24px', 
          padding: '16px', 
          background: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: '1.4' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
            Make sure your microphone is enabled. The call will connect through your browser.
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebCallModal; 